import type Phaser from 'phaser';
import type { PvpRuneRecipe } from '../pvp/rune-appearance';

/**
 * As nove texturas procedurais de que as três runas são feitas.
 *
 * ## Em escala de cinza, de propósito
 *
 * Cada receita é pintada em BRANCO com o seu próprio sombreamento, nunca na cor
 * de uma runa, e o compositor tinge o sprite. Um tint MULTIPLICA, então uma
 * faceta branca vira a cor da paleta e uma faceta 60% cinza vira uma versão
 * escura da mesma cor - que é exatamente a relação entre a face iluminada e a
 * face na sombra de um sólido. Pintar a cor dentro significaria nove texturas
 * por runa em vez de nove no total.
 *
 * ## Assadas uma vez, num tamanho só
 *
 * Toda receita é assada em {@link RUNE_TEXTURE_SIZE} e escalada pelo
 * compositor. A alternativa - regerar no tamanho desenhado sempre que a câmera
 * dá zoom - alocaria um canvas durante um passo da roda, que é a única coisa
 * que um renderer não pode fazer com uma luta na tela.
 *
 * ## Por que nenhum shader
 *
 * O volume vem de camadas, do sombreamento assado na textura, de um aperto de
 * cosseno em `scaleX` e do parallax entre camadas. Isso bastou para as três, e
 * a saída de emergência do gate V25A - parar antes de implementar um shader -
 * nunca precisou ser usada. Sem `postFX`, sem pipeline, sem passe de tela cheia.
 */

/** O lado de cada receita assada, em pixels. */
export const RUNE_TEXTURE_SIZE = 128;

/** O ponto de resíduo é pequeno e não há por que assá-lo grande. */
export const RUNE_MOTE_TEXTURE_SIZE = 32;

/** Toda textura deste módulo se chama `keno-rune-<receita>`. */
export const RUNE_TEXTURE_PREFIX = 'keno-rune-';

export function runeTextureKey(recipe: PvpRuneRecipe): string {
  return `${RUNE_TEXTURE_PREFIX}${recipe}`;
}

/** O mínimo que este módulo precisa de uma cena. Testável sem um jogo. */
export interface RuneTextureScene {
  readonly textures: {
    exists(key: string): boolean;
    remove(key: string): unknown;
  };
  readonly make: {
    graphics(config: unknown, addToScene: boolean): Phaser.GameObjects.Graphics;
  };
}

type Painter = (graphics: Phaser.GameObjects.Graphics, size: number) => void;

const TAU = Math.PI * 2;

/**
 * Uma queda radial, como preenchimentos concêntricos.
 *
 * O `Graphics` do Phaser não tem gradiente, e um gradiente de canvas seria uma
 * segunda API de desenho neste arquivo. Trinta e dois anéis com alfa decrescente
 * são visualmente indistinguíveis de um gradiente depois que a textura é
 * escalada, e são desenhados exatamente uma vez na vida da página.
 */
function radialFalloff(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  radius: number,
  innerAlpha: number,
  power: number,
): void {
  const steps = 32;
  for (let step = steps; step >= 1; step -= 1) {
    const t = step / steps;
    const alpha = innerAlpha * (1 - t) ** power;
    if (alpha <= 0.002) {
      continue;
    }
    graphics.fillStyle(0xffffff, alpha);
    graphics.fillCircle(cx, cy, radius * t);
  }
  graphics.fillStyle(0xffffff, innerAlpha);
  graphics.fillCircle(cx, cy, radius * 0.14);
}

/** Um polígono a partir de pontos já no espaço da textura. */
function polygon(
  graphics: Phaser.GameObjects.Graphics,
  points: readonly (readonly [number, number])[],
  grey: number,
  alpha: number,
): void {
  graphics.fillStyle(grey, alpha);
  graphics.beginPath();
  const first = points[0];
  if (!first) {
    return;
  }
  graphics.moveTo(first[0], first[1]);
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];
    if (point) {
      graphics.lineTo(point[0], point[1]);
    }
  }
  graphics.closePath();
  graphics.fillPath();
}

/**
 * Um anel fechado, e por que não é `arc(0, TAU)`.
 *
 * Um caminho de zero até uma volta inteira é um arco degenerado - o começo e o
 * fim são o mesmo ponto - e o Phaser não desenha nada. As primeiras capturas do
 * laboratório V25A não tinham selo mágico nenhum por exatamente isso: os anéis
 * em escala de cinza estavam vazios, e não sutis. `strokeCircle` é a primitiva
 * que fecha.
 */
function ring(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  radius: number,
  width: number,
  grey: number,
  alpha: number,
): void {
  graphics.lineStyle(width, grey, alpha);
  graphics.strokeCircle(cx, cy, radius);
}

/** Um arco desenhado como um caminho traçado grosso. */
function arc(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  radius: number,
  from: number,
  to: number,
  width: number,
  grey: number,
  alpha: number,
): void {
  graphics.lineStyle(width, grey, alpha);
  graphics.beginPath();
  graphics.arc(cx, cy, radius, from, to, false);
  graphics.strokePath();
}

const PAINTERS: Readonly<Record<PvpRuneRecipe, Painter>> = Object.freeze({
  'core-glow': (graphics, size) => {
    const c = size / 2;
    radialFalloff(graphics, c, c, c * 0.94, 0.85, 2.6);
  },

  /*
   * Uma esfera: um disco claro, um terminador escurecendo para baixo e para a
   * direita, e uma luz de borda no lado sombreado.
   *
   * A luz de borda é o que impede a leitura de "disco chato com uma sombra
   * pintada em cima".
   */
  'orb-shaded': (graphics, size) => {
    const c = size / 2;
    const r = c * 0.9;
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(c, c, r);
    /*
     * O terminador desce, e não desce PARA A DIREITA.
     *
     * A esfera era iluminada do alto à esquerda: o especular ficava a −0,32 do
     * raio e a sombra escorria para baixo e para a direita. Isso é sombreamento
     * legítimo de um sólido, e produzia um viés medível - a faixa do núcleo da
     * Coroa Incandescente pendia −0,19 para a esquerda em MÉDIA ao longo de uma
     * volta inteira da órbita das lâminas, o que é viés e não fase.
     *
     * A luz passou a vir de CIMA. A esfera continua sendo uma esfera - há
     * especular, terminador, gradiente e luz de borda, que é o que dá volume -
     * mas nada disso tem componente horizontal, então o centro óptico coincide
     * com o geométrico. É a mesma regra que o selo já seguia: a fundação é
     * simétrica, a decoração pode não ser.
     */
    const steps = 22;
    for (let step = 0; step < steps; step += 1) {
      const t = step / steps;
      const shade = 1 - t;
      graphics.fillStyle(0x000000, 0.055);
      graphics.fillCircle(c, c + r * 0.42 * (1 - shade) + r * 0.2, r * (0.96 - t * 0.16));
    }
    /* Especular, no alto e no eixo. */
    graphics.fillStyle(0xffffff, 0.45);
    graphics.fillEllipse(c, c - r * 0.34, r * 0.5, r * 0.3);
    /* Luz de borda na parte de baixo, simétrica em torno do eixo. */
    graphics.lineStyle(Math.max(1, size * 0.018), 0xffffff, 0.55);
    graphics.beginPath();
    graphics.arc(c, c, r * 0.97, TAU * 0.06, TAU * 0.44, false);
    graphics.strokePath();
  },

  /*
   * Um cristal facetado: quatro faces em torno de um eixo vertical, iluminado
   * do alto à esquerda.
   *
   * Os cinzas são CLAROS, e a primeira versão errou nisso. Um tint multiplica,
   * então uma face `0x3a` vira 23% da cor da paleta - contra um piso quase
   * preto, indistinguível de nada. O cristal lia como um triângulo brilhante
   * com duas faces invisíveis. Começar o mais escuro em `0x6e` mantém todas as
   * quatro na tela e ainda as ordena: iluminada, corpo, sombreada, de trás.
   */
  'facet-shard': (graphics, size) => {
    const c = size / 2;
    const top: readonly [number, number] = [c, size * 0.04];
    const bottom: readonly [number, number] = [c, size * 0.96];
    const left: readonly [number, number] = [size * 0.16, c * 0.98];
    const right: readonly [number, number] = [size * 0.84, c * 1.02];
    const mid: readonly [number, number] = [c * 1.06, c];
    polygon(graphics, [top, left, mid], 0xffffff, 1);
    polygon(graphics, [top, mid, right], 0xd2d2d2, 1);
    polygon(graphics, [bottom, left, mid], 0xa0a0a0, 1);
    polygon(graphics, [bottom, mid, right], 0x6e6e6e, 1);
    graphics.lineStyle(Math.max(1, size * 0.012), 0xffffff, 0.85);
    graphics.beginPath();
    graphics.moveTo(top[0], top[1]);
    graphics.lineTo(mid[0], mid[1]);
    graphics.lineTo(bottom[0], bottom[1]);
    graphics.strokePath();
  },

  /** Uma lâmina: larga na base, pontuda na ponta, mais clara na espinha. */
  blade: (graphics, size) => {
    const c = size / 2;
    polygon(
      graphics,
      [
        [c, size * 0.03],
        [size * 0.72, size * 0.62],
        [c, size * 0.97],
        [size * 0.28, size * 0.62],
      ],
      0xc8c8c8,
      1,
    );
    polygon(
      graphics,
      [
        [c, size * 0.06],
        [size * 0.6, size * 0.62],
        [c, size * 0.9],
      ],
      0xffffff,
      0.95,
    );
    polygon(
      graphics,
      [
        [c, size * 0.06],
        [size * 0.4, size * 0.62],
        [c, size * 0.9],
      ],
      0x555555,
      0.55,
    );
  },

  ring: (graphics, size) => {
    const c = size / 2;
    ring(graphics, c, c, c * 0.82, Math.max(2, size * 0.05), 0xffffff, 0.95);
    ring(graphics, c, c, c * 0.82, Math.max(1, size * 0.13), 0xffffff, 0.24);
  },

  /*
   * Um anel com quatro cortes - sobre um aro contínuo que o ancora.
   *
   * ## Por que o aro de baixo existe
   *
   * Quatro arcos têm simetria de quatro dobras: eles só ficam simétricos em
   * relação ao eixo vertical quando a rotação é múltipla de 45°. Girando, o
   * peso luminoso balança de um lado para o outro, e a Ponta Vetorial media
   * exatamente isso - o desequilíbrio da faixa do selo indo de −0,32 a +0,30
   * conforme a fase, com o centro óptico oscilando quase 10 px.
   *
   * Um aro CONTÍNUO é simétrico em qualquer rotação, por construção. Ele entra
   * fino e discreto sob os quatro arcos: a leitura continua sendo "um anel
   * quebrado" - os cortes seguem visíveis, porque o que os marca é o contraste
   * entre o traço grosso e o fino - e o centro óptico deixa de balançar.
   *
   * É a mesma regra que o gate enuncia: a fundação é simétrica, a decoração
   * pode não ser.
   */
  'ring-broken': (graphics, size) => {
    const c = size / 2;
    const width = Math.max(2, size * 0.05);
    ring(graphics, c, c, c * 0.82, Math.max(1, size * 0.016), 0xffffff, 0.5);
    for (let quarter = 0; quarter < 4; quarter += 1) {
      const from = quarter * (TAU / 4) + TAU * 0.03;
      const to = from + TAU / 4 - TAU * 0.06;
      arc(graphics, c, c, c * 0.82, from, to, width, 0xffffff, 0.95);
      arc(graphics, c, c, c * 0.82, from, to, width * 2.2, 0xffffff, 0.2);
    }
  },

  /*
   * O selo mágico: dois anéis e doze marcas.
   *
   * Pequeno, sóbrio e deliberadamente não um diagrama de invocação - diz "o pad
   * está segurando alguma coisa" e para por aí. O compositor o achata, então o
   * que é desenhado aqui como círculo aterrissa como plano.
   */
  sigil: (graphics, size) => {
    const c = size / 2;
    ring(graphics, c, c, c * 0.88, Math.max(2, size * 0.034), 0xffffff, 0.95);
    ring(graphics, c, c, c * 0.6, Math.max(1, size * 0.022), 0xffffff, 0.6);
    graphics.lineStyle(Math.max(2, size * 0.03), 0xffffff, 0.85);
    for (let tick = 0; tick < 12; tick += 1) {
      const angle = (tick / 12) * TAU;
      const inner = c * 0.66;
      const outer = tick % 3 === 0 ? c * 0.86 : c * 0.76;
      graphics.beginPath();
      graphics.moveTo(c + Math.cos(angle) * inner, c + Math.sin(angle) * inner);
      graphics.lineTo(c + Math.cos(angle) * outer, c + Math.sin(angle) * outer);
      graphics.strokePath();
    }
  },

  arrowhead: (graphics, size) => {
    const c = size / 2;
    polygon(
      graphics,
      [
        [c, size * 0.05],
        [size * 0.9, size * 0.72],
        [c, size * 0.56],
        [size * 0.1, size * 0.72],
      ],
      0xffffff,
      1,
    );
    polygon(
      graphics,
      [
        [c, size * 0.05],
        [size * 0.9, size * 0.72],
        [c, size * 0.56],
      ],
      0x8c8c8c,
      1,
    );
    graphics.lineStyle(Math.max(1, size * 0.016), 0xffffff, 0.9);
    graphics.beginPath();
    graphics.moveTo(c, size * 0.05);
    graphics.lineTo(c, size * 0.56);
    graphics.strokePath();
  },

  mote: (graphics, size) => {
    const c = size / 2;
    radialFalloff(graphics, c, c, c * 0.92, 0.95, 2);
  },
});

/**
 * Assa uma receita se ela ainda não estiver assada, e devolve a sua chave.
 *
 * Idempotente pela chave da textura. O `Graphics` usado para pintar é destruído
 * antes de retornar, então nada sobra na display list - a textura é a única
 * sobrevivente, e {@link destroyRuneTextures} é o que a remove.
 */
export function ensureRuneTexture(scene: RuneTextureScene, recipe: PvpRuneRecipe): string {
  const key = runeTextureKey(recipe);
  if (scene.textures.exists(key)) {
    return key;
  }
  const size = recipe === 'mote' ? RUNE_MOTE_TEXTURE_SIZE : RUNE_TEXTURE_SIZE;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  PAINTERS[recipe](graphics, size);
  graphics.generateTexture(key, size, size);
  graphics.destroy();
  return key;
}

/** Toda receita, assada. Chamado uma vez, quando a arena abre. */
export function ensureAllRuneTextures(
  scene: RuneTextureScene,
  recipes: readonly PvpRuneRecipe[],
): string[] {
  return recipes.map((recipe) => ensureRuneTexture(scene, recipe));
}

/**
 * Remove toda textura que este módulo fez.
 *
 * Por nome, e não "tudo que tem o prefixo e por acaso existe": o renderer tem de
 * poder dizer que deixou o gerenciador de texturas exatamente como o encontrou,
 * e uma varredura por prefixo removeria também uma textura que outra parte da
 * página tivesse assado com nome parecido.
 */
export function destroyRuneTextures(scene: RuneTextureScene, keys: readonly string[]): number {
  let removed = 0;
  for (const key of keys) {
    if (scene.textures.exists(key)) {
      scene.textures.remove(key);
      removed += 1;
    }
  }
  return removed;
}
